import { useState, useEffect, useCallback, useRef } from 'react';
import { getApp } from 'firebase/app';
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { userLearningState } from '../core/firestorePaths.js';
import { shouldAutoOpenRoleOrientation, buildRoleIntroCompletionUpdate, isLearningStateResolved } from './roleOrientationAutoOpen.js';

function getDb() {
  return getFirestore(getApp());
}

export function useRoleLearningState(uid, tenantId, role) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [learningState, setLearningState] = useState(null);
  // The uid whose learning-state read has resolved. Until this matches the
  // current `uid`, the state is "not yet loaded" — even when `loading` is a stale
  // false during the login uid-transition. Guards the auto-open read race.
  const [loadedUid, setLoadedUid] = useState(null);
  const [sessionDismissed, setSessionDismissed] = useState(false);
  const [sessionDismissedModules, setSessionDismissedModules] = useState({});
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      setError(null);
      setLearningState(null);
      setLoadedUid(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const path = userLearningState(uid);
    getDoc(doc(getDb(), path))
      .then(snap => {
        if (cancelled) return;
        if (snap.exists()) {
          setLearningState(snap.data());
        } else {
          setLearningState(null);
        }
        setError(null);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err);
        setLearningState(null);
      })
      .finally(() => {
        if (cancelled) return;
        // Mark THIS uid's read resolved (success, empty, or error) so the
        // auto-open decision may run — never against a stale/empty pre-read state.
        setLoadedUid(uid);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [uid]);

  // Durable Role-Orientation completion — the writer behind the AUTO-OPEN card's
  // "I understand" button. Persists BOTH source-of-truth markers in a single
  // merge: `roleIntroCompleted` (primary) and the set-once
  // `roleOrientationAutoOpenedAt` (only when absent), so one click makes future
  // auto-open impossible (see shouldAutoOpenRoleOrientation). Only `uid` is
  // required — a missing tenantId/role must never block durable completion.
  // Identity + defaults are stamped ONLY when first creating the doc and are
  // never overwritten on later merges (preserves uid/tenantId/role/createdAt).
  const completeRoleIntro = useCallback(async (currentRoute) => {
    if (!uid) {
      throw new Error('Missing uid');
    }

    const path = userLearningState(uid);
    const ref = doc(getDb(), path);

    const update = buildRoleIntroCompletionUpdate({
      existing: learningState,
      currentRoute,
      now: serverTimestamp(),
    });

    const isNew = !learningState;
    if (isNew) {
      update.uid = uid;
      if (tenantId) update.tenantId = tenantId;
      if (role) update.role = role;
      update.learningModeEnabled = true;
      update.createdAt = serverTimestamp();
    }

    try {
      await setDoc(ref, update, { merge: true });
    } catch (err) {
      // Fail-fast: surface the durable-completion write failure; do NOT pretend
      // it succeeded (the optimistic flip below is skipped). The caller keeps the
      // modal open for retry, and on a future login auto-open may recur because
      // nothing durable persisted.
      console.error('[learning] completeRoleIntro durable write failed:', err?.message || err);
      throw err;
    }

    if (mountedRef.current) {
      setLearningState(prev => ({
        ...(prev || {}),
        roleIntroCompleted: true,
        roleOrientationAutoOpenedAt:
          prev?.roleOrientationAutoOpenedAt != null ? prev.roleOrientationAutoOpenedAt : 'pending',
        completedModules: {
          ...(prev?.completedModules || {}),
          role_orientation: true,
        },
        lastSeenRoute: currentRoute || null,
      }));
    }
  }, [uid, tenantId, role, learningState]);

  // Role Orientation auto-open marker (Option A). Set-once: writes the durable
  // `roleOrientationAutoOpenedAt` to users/{uid}/learning/state the FIRST time
  // orientation auto-opens, so it never auto-opens again (cross-device). Manual
  // opening from Settings never calls this. Optimistic local update first, so a
  // failed write cannot cause a same-session re-open loop. Preserves all existing
  // learning fields via merge.
  const markOrientationAutoOpened = useCallback(async () => {
    if (!uid) return;
    if (learningState?.roleOrientationAutoOpenedAt != null) return; // already marked — set-once

    if (mountedRef.current) {
      setLearningState(prev => ({ ...(prev || {}), roleOrientationAutoOpenedAt: 'pending' }));
    }

    const path = userLearningState(uid);
    const ref = doc(getDb(), path);
    try {
      await setDoc(ref, {
        roleOrientationAutoOpenedAt: serverTimestamp(),
        lastUpdatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (err) {
      // Fail safely: keep the optimistic in-memory marker (no same-session loop);
      // the durable write may retry on a future login. Never silently "succeed".
      console.warn('[learning] markOrientationAutoOpened write failed:', err?.message || err);
    }
  }, [uid, learningState]);

  const completeLearningStep = useCallback(async (moduleId, stepId) => {
    if (!uid) throw new Error('Missing uid');

    const key = `${moduleId}__${stepId}`;

    setLearningState(prev => ({
      ...prev,
      completedSteps: {
        ...(prev?.completedSteps || {}),
        [key]: true,
      },
    }));

    const path = userLearningState(uid);
    const ref = doc(getDb(), path);
    const update = {
      [`completedSteps.${key}`]: true,
      lastUpdatedAt: serverTimestamp(),
    };

    setDoc(ref, update, { merge: true }).catch(() => {
      if (mountedRef.current) {
        setLearningState(prev => {
          const next = { ...prev, completedSteps: { ...(prev?.completedSteps || {}) } };
          delete next.completedSteps[key];
          return next;
        });
      }
    });
  }, [uid]);

  const completeLearningModule = useCallback(async (moduleId) => {
    if (!uid) throw new Error('Missing uid');

    setLearningState(prev => ({
      ...prev,
      completedModules: {
        ...(prev?.completedModules || {}),
        [moduleId]: true,
      },
    }));

    const path = userLearningState(uid);
    const ref = doc(getDb(), path);
    const update = {
      [`completedModules.${moduleId}`]: true,
      lastUpdatedAt: serverTimestamp(),
    };

    setDoc(ref, update, { merge: true }).catch(() => {
      if (mountedRef.current) {
        setLearningState(prev => {
          const next = { ...prev, completedModules: { ...(prev?.completedModules || {}) } };
          delete next.completedModules[moduleId];
          return next;
        });
      }
    });
  }, [uid]);

  const dismissLearningModule = useCallback((moduleId) => {
    setSessionDismissedModules(prev => ({ ...prev, [moduleId]: true }));
  }, []);

  const undismissLearningModule = useCallback((moduleId) => {
    setSessionDismissedModules(prev => {
      const next = { ...prev };
      delete next[moduleId];
      return next;
    });
  }, []);

  const setLearningModeEnabled = useCallback(async (enabled) => {
    if (!uid) throw new Error('Missing uid');

    const path = userLearningState(uid);
    const ref = doc(getDb(), path);

    const update = {
      learningModeEnabled: enabled,
      lastUpdatedAt: serverTimestamp(),
    };

    await setDoc(ref, update, { merge: true });

    if (mountedRef.current) {
      setLearningState(prev => ({
        ...prev,
        learningModeEnabled: enabled,
      }));
    }
  }, [uid]);

  const dismissForSession = useCallback(() => {
    setSessionDismissed(true);
  }, []);

  // Treat the state as loading until the CURRENT uid's read has resolved. On
  // login, `uid` is available one render before its read lands; without this gate
  // `loading` is a stale false and `learningState` is null, so the rule below
  // would fire against an empty snapshot and re-open orientation every login
  // regardless of the persisted markers. (Root cause of the reopen bug.)
  const effectiveLoading = loading || !isLearningStateResolved(uid, loadedUid);

  const shouldShowOrientation = !effectiveLoading
    && !error
    && !sessionDismissed
    && learningState?.roleIntroCompleted !== true;

  // Option A auto-open: absent durable marker AND not already acknowledged.
  const shouldAutoOpenOrientation = shouldAutoOpenRoleOrientation({
    loading: effectiveLoading,
    error,
    sessionDismissed,
    roleOrientationAutoOpenedAt: learningState?.roleOrientationAutoOpenedAt,
    roleIntroCompleted: learningState?.roleIntroCompleted,
  });

  const isStepCompleted = useCallback((moduleId, stepId) => {
    const key = `${moduleId}__${stepId}`;
    return learningState?.completedSteps?.[key] === true;
  }, [learningState]);

  const isModuleCompleted = useCallback((moduleId) => {
    return learningState?.completedModules?.[moduleId] === true;
  }, [learningState]);

  const isModuleDismissedForSession = useCallback((moduleId) => {
    return sessionDismissedModules[moduleId] === true;
  }, [sessionDismissedModules]);

  const learningModeEnabled = learningState?.learningModeEnabled !== false;

  return {
    loading,
    error,
    learningState,
    shouldShowOrientation,
    shouldAutoOpenOrientation,
    markOrientationAutoOpened,
    learningModeEnabled,
    completeRoleIntro,
    completeLearningStep,
    completeLearningModule,
    dismissLearningModule,
    undismissLearningModule,
    setLearningModeEnabled,
    dismissForSession,
    isStepCompleted,
    isModuleCompleted,
    isModuleDismissedForSession,
  };
}
